import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/apiClient";
import { displayDoctorName } from "@/lib/utils";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, Search, Loader2, ArrowLeft } from "lucide-react";

export default function RetrievePage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [found, setFound] = useState(null);

  const search = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Enter both Name and Email."); return; }
    setLoading(true);
    setFound(null);
    try {
      const res = await api.post("/pledges/retrieve", { name, email });
      setFound(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-xl mx-auto px-5 py-16">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0056D2] mb-6" data-testid="retrieve-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="rounded-3xl bg-white border border-slate-200 p-8">
          <h1 className="font-display font-extrabold text-3xl text-slate-900">Retrieve Certificate</h1>
          <p className="mt-2 text-slate-500">Enter the details you used while pledging to download your certificate again.</p>
          <div className="mt-8 space-y-5">
            <div>
              <Label htmlFor="r-name" className="text-sm font-medium text-slate-700">Full Name</Label>
              <Input data-testid="retrieve-name" id="r-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" placeholder="Dr. Full Name (as entered while pledging)" />
            </div>
            <div>
              <Label htmlFor="r-email" className="text-sm font-medium text-slate-700">Email</Label>
              <Input data-testid="retrieve-email" id="r-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="you@example.com" />
            </div>
            <Button data-testid="retrieve-search-btn" onClick={search} disabled={loading} size="lg" className="w-full rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold h-12">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching…</> : <><Search className="w-4 h-4 mr-2" /> Find my certificate</>}
            </Button>
          </div>

          {found && (
            <div data-testid="retrieve-result" className="mt-8 rounded-2xl border border-green-200 bg-green-50 p-6 text-center">
              <p className="text-slate-600 text-sm">Certificate found for</p>
              <p className="font-display font-bold text-xl text-slate-900 mt-1">{displayDoctorName(found.name)}</p>
              <p className="text-sm text-[#0056D2] font-medium mt-1">{found.certificate_id}</p>
              <a href={`${API}/certificate/${found.certificate_id}`} data-testid="retrieve-download-btn" className="inline-flex mt-5">
                <Button className="rounded-full bg-[#138808] hover:bg-green-700 text-white font-semibold px-6">
                  <Download className="w-4 h-4 mr-2" /> Download Certificate
                </Button>
              </a>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
